import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Trash2, Mail, CalendarDays, Eye } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ClientDetailDialog } from "../../components/ClientDetailDialog";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const statusVariant = { submitted: "default", in_review: "secondary", completed: "outline" };

export default function ClientsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clients, setClients] = useState([]);
  const [intakes, setIntakes] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/clients"), api.get("/intakes")])
      .then(([c, i]) => {
        setClients(c.data);
        const map = {};
        i.data.forEach((x) => { map[x.user_id] = x.status; });
        setIntakes(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const removeClient = async (c) => {
    try {
      await api.delete(`/clients/${c.user_id}`);
      toast.success(`${c.name} removed from the system`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="clients-page" className="space-y-8 animate-fade-up">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">
          {isAdmin ? "All clients in the system" : "Your assigned clients"}
        </p>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight mt-1">Client List</h1>
      </div>
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : clients.length === 0 ? (
            <div className="p-10 text-center" data-testid="no-clients-message">
              <Users className="w-10 h-10 text-muted-foreground/40 mx-auto" />
              <p className="text-sm text-muted-foreground mt-3">
                {isAdmin ? "No clients registered yet." : "No clients assigned to you yet — clients appear here once they book an appointment with you."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="clients-table">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="p-4 font-medium text-muted-foreground">Client</th>
                    <th className="p-4 font-medium text-muted-foreground">Joined</th>
                    <th className="p-4 font-medium text-muted-foreground">Intake Status</th>
                    <th className="p-4 font-medium text-muted-foreground text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.user_id} className="border-b border-border last:border-0" data-testid={`client-row-${c.user_id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center font-heading text-lg font-semibold text-primary shrink-0">
                            {c.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{c.name}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">
                        <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {c.created_at?.slice(0, 10)}</span>
                      </td>
                      <td className="p-4">
                        {intakes[c.user_id] ? (
                          <Badge variant={statusVariant[intakes[c.user_id]] || "default"} className="capitalize" data-testid={`client-intake-${c.user_id}`}>
                            {intakes[c.user_id].replace("_", " ")}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground" data-testid={`client-intake-${c.user_id}`}>Not submitted</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button className="text-muted-foreground hover:text-primary transition-colors" data-testid={`client-view-${c.user_id}`} title="View client information"
                            onClick={() => setSelected(c)}>
                            <Eye className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`client-delete-${c.user_id}`} title="Remove client">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid={`client-delete-dialog-${c.user_id}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove {c.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This permanently removes the client account, their intake, online form submissions, and appointments. Uploaded documents are archived. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`client-delete-cancel-${c.user_id}`}>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => removeClient(c)} data-testid={`client-delete-confirm-${c.user_id}`} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  Remove Client
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          )}
                        </div>
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <ClientDetailDialog client={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
}
